<template>
  <ModalContainer
    :is-show-modal="isShowModal"
    :styles="{ 'facility-object-list-modal': true }"
    :is-disabled-teleport="isDisabledTeleport"
    v-model:modal-body="modalBody"
    @close="closeModal"
  >
    <template #header>
      <div
        class="facility-object-list-modal__head"
      >
        <BaseInput
          v-model="searchValue"
          no-error
          clearable
          icon="search"
          :inner-placeholder="$t('Search')"
          class="facility-object-list-modal__search"
        />
        <BaseButton
          text
          @click="closeModal"
          class="facility-object-list-modal__cancel"
        >{{ $t("Cancel") }}</BaseButton
        >
      </div>

      <div
        class="facility-object-list-modal__head facility-object-list-modal__head_desktop"
      >
        <div class="facility-list__title">
          <Transition
            name="facility-object-modal-title-slide-up"
            mode="out-in"
          >
            <p :key="titleToShow">{{ titleToShow }}</p>
          </Transition>
        </div>
        <IconButton
          class="facility-object-list-modal__close"
          icon="cross"
          @click="closeModal"
        />
      </div>
    </template>
    <div>
      <div class="facility-list">
        <div v-if="isLoading" class="facility-list-spinner">
          <SpinnerRadial />
        </div>
        <template v-else-if="list.length">
          <div class="facility-list__title tablet">
            <Transition
              name="facility-object-modal-title-slide-up"
              mode="out-in"
            >
              <p :key="titleToShow">{{ titleToShow }}</p>
            </Transition>
          </div>
          <template v-for="item in list">
            <div class="facility-list__subtitle" v-if="item.title">
              <p ref="subtitles">{{ item.title }}</p>
            </div>
            <div
              v-for="facility in item.elements"
              :key="facility.id"
              class="facility-list-item"
              @click="selectFacilityObject(facility)"
            >
              <p>{{ facility.name }}</p>
              <p>{{ facility.address }}</p>
            </div>
          </template>
        </template>
      </div>
    </div>
  </ModalContainer>
</template>

<script lang="ts">
import { PropType } from "vue";
import { ISupplier, ISupplierPreview } from "~/types/map.interface";

import ModalContainer from "~/components/modals/ModalContainer.vue";
import BaseButton from "~/components/button/BaseButton.vue";
import BaseInput from "~/components/input/BaseInput.vue";
import SpinnerRadial from "~/components/loaders/SpinnerRadial.vue";
import IconButton from "../button/IconButton.vue";

export default {
  components: {
    SpinnerRadial,
    BaseInput,
    BaseButton,
    ModalContainer,
    IconButton,
  },

  props: {
    modalTitle: {
      type: String as PropType<string>,
      required: true,
    },
    list: {
      type: Array as PropType<{ title?: string, elements: (ISupplier | ISupplierPreview)[] }[]>,
      required: true
    },
    searchInput: {
      type: String as PropType<string | null>,
      default: null,
    },
    isLoading: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    isDisabledTeleport: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    isShowModal: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
  },
  emits: ["update:searchInput", "update:isShowModal", "selectFacilityObject", "close"],

  setup(props, { emit }) {
    const searchValue = computed({
      get() {
        return props.searchInput;
      },
      set(val) {
        emit("update:searchInput", val);
      },
    });

    function closeModal() {
      emit("update:isShowModal", false);
      emit("update:searchInput", "");
      emit("close");
    }
    function selectFacilityObject(facility: ISupplier | ISupplierPreview) {
      emit("selectFacilityObject", {
        id: facility.id,
        name: facility.name,
        address: facility.address,
        coordinates: facility.coordinates,
      } as ISupplierPreview);
    }

    const nearestTitle = ref<string | null>(null);
    const titleToShow = computed(() => {
      return nearestTitle.value || props.modalTitle;
    });

    const subtitles = ref<HTMLElement[]>([]);

    const switchTitle = () => {
      if (modalBody.value) {
        let nearestSubtitleToModalBody: HTMLElement | null = null;
        const modalBodyParams = modalBody.value.getBoundingClientRect();

        for (const subtitle of subtitles.value) {
          const { y: modalBodyY } = modalBodyParams;
          const { y: subtitleY } = subtitle.getBoundingClientRect();

          const isSubtitleAboveTheModal = modalBodyY > subtitleY;
          if (isSubtitleAboveTheModal) {
            if (!nearestSubtitleToModalBody) {
              nearestSubtitleToModalBody = subtitle;
            } else if (subtitleY > nearestSubtitleToModalBody.getBoundingClientRect().y) {
              nearestSubtitleToModalBody = subtitle;
            }
          }
        }

        nearestTitle.value = nearestSubtitleToModalBody?.textContent ?? null;
      }
    };

    function throttle<T extends (...args: any[]) => void, K>(func: T, delay: number) {
      let lastCall = 0;
      let isDirty = false;

      const throttledFunction = function(this: K, ...args: Parameters<T>) {
        const now = Date.now();

        if (now - lastCall >= delay) {
          lastCall = now;
          isDirty = false;
          func.apply(this, args);
        } else {
          isDirty = true;
        }
      };

      throttledFunction.isDirty = () => isDirty;

      return throttledFunction;
    }

    const throttledSwitchTitle  = throttle(switchTitle, 1000);
    const onScrollEnd = () => {
      if (throttledSwitchTitle.isDirty()) {
        switchTitle();
      }
    };

    let abortController = new AbortController();

    const modalBody = ref<HTMLElement | null>(null);
    watch(() => [!!modalBody.value, props.list], () => {
      abortController.abort();
      nearestTitle.value = null;

      const hasMultipleItems = props.list.length > 1;
      if (modalBody.value && hasMultipleItems) {
        abortController = new AbortController();
        modalBody.value.addEventListener("scroll", throttledSwitchTitle, { signal: abortController.signal });
        modalBody.value.addEventListener("scrollend", onScrollEnd, { signal: abortController.signal });
      }
    }, { deep: false });

    onUnmounted(() => {
      abortController.abort();
    });

    return {
      searchValue,
      modalBody,
      subtitles,
      titleToShow,
      closeModal,
      selectFacilityObject
    };
  },
};
</script>
